import React from 'react';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import './ScrollableSelect.scss';

export interface ScrollableSelectOption {
    text: string;
    value: string;
}

export interface ScrollableSelectProps {
    label: string;
    value: string;
    options: ScrollableSelectOption[];
    onChange: (value: string) => void;
    onScrollEnd: () => Promise<void>;
    className?: string;
}

@observer
export class ScrollableSelect extends TechseeBaseComponent<ScrollableSelectProps> {
    @observable private _isOpen: boolean = false;

    private readonly _dropdownRef = React.createRef<HTMLDivElement>();
    private readonly _containerRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        document.addEventListener('mousedown', this._handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._handleClickOutside);
    }

    private _handleClickOutside = (e: MouseEvent) => {
        if (this._containerRef.current && !this._containerRef.current.contains(e.target as Node)) {
            this._close();
        }
    };

    @action
    private _close() {
        this._isOpen = false;
    }

    @action
    private _toggleOpen = () => {
        this._isOpen = !this._isOpen;
    };

    @action
    private _selectOption = (value: string) => {
        this.props.onChange(value);
        this._isOpen = false;
    };

    private _handleScroll = () => {
        const el = this._dropdownRef.current;

        if (!el) {
            return;
        }
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            this.props.onScrollEnd();
        }
    };

    renderInternal(): JSX.Element {
        const {label, value, options, className} = this.props;
        const selectedLabel = options.find((o) => o.value === value)?.text ?? '';

        return (
            <div ref={this._containerRef} className={`scrollable-select${className ? ` ${className}` : ''}`}>
                <span className='scrollable-select-label'>{label}</span>
                <div className='scrollable-select-trigger' onClick={this._toggleOpen}>
                    <span className='scrollable-select-value'>{selectedLabel || '—'}</span>
                    <span className={`scrollable-select-arrow${this._isOpen ? ' open' : ''}`}>▾</span>
                </div>
                {this._isOpen && (
                    <div className='scrollable-select-dropdown' ref={this._dropdownRef} onScroll={this._handleScroll}>
                        {options.map((opt) => (
                            <div
                                key={opt.value}
                                className={`scrollable-select-option${opt.value === value ? ' selected' : ''}`}
                                onMouseDown={() => this._selectOption(opt.value)}>
                                {opt.text}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}
